// AdminPaymentPage.jsx

import React, { useState, useEffect } from 'react';
import API from '../services/api';

// ============== Utility: replicate AdminDailyReport approach ==============

// 1) color-coded row style
function getTherapistRowStyle(therapistName = '', colorMap) {
  if (!therapistName.trim()) {
    return { backgroundColor: '#f5f5f5' };
  }
  const payObj = colorMap[therapistName];
  if (payObj && payObj.colorCode) {
    return { backgroundColor: payObj.colorCode };
  }
  return { backgroundColor: '#f5f5f5' };
}

// 2) Payment Type highlight
function getPaymentStyle(paymentType) {
  const lower = (paymentType || '').toLowerCase();
  let bg = 'lightgray';
  let fg = '#000';
  if (lower.includes('cash')) {
    bg = 'green';
    fg = '#fff';
  } else if (lower.includes('card')) {
    bg = 'blue';
    fg = '#fff';
  } else if (lower.includes('check')) {
    bg = 'yellow';
    fg = '#000';
  }
  return {
    backgroundColor: bg,
    color: fg,
    padding: '2px 6px',
    borderRadius: '4px',
    display: 'inline-block',
  };
}

// Utility: format date
function formatSessionDate(rawDate) {
  if (!rawDate) return '';
  const dateString = rawDate.includes('T') ? rawDate : rawDate + 'T12:00:00';
  return new Date(dateString).toLocaleDateString();
}

// ============== The AdminPaymentPage component ==============
function AdminPaymentPage() {
  // 1) States for Unpaid & Debt sessions
  const [unpaidSessions, setUnpaidSessions] = useState([]);
  const [debtSessions, setDebtSessions] = useState([]);
  const [editingRows, setEditingRows] = useState({}); // track in-line editing

  // 2) Payment Profiles: clientId => [ { ...profile }, ... ]
  const [clientProfiles, setClientProfiles] = useState({});

  // 3) Therapist color map: "FirstName LastName" => { colorCode }
  const [therapistColors, setTherapistColors] = useState({});

  // 4) Manage Payment Info forms
  // A) Create brand-new profile
  const [newProfileForm, setNewProfileForm] = useState({
    clientId: '',
    cardNumber: '',
    expirationDate: '',
    cardCode: '',
    firstName: '',
    lastName: '',
    companyName: '',
  });
  // B) Link existing
  const [linkProfileClientId, setLinkProfileClientId] = useState('');
  const [existingProfileId, setExistingProfileId] = useState('');
  // C) Add card
  const [addCardForm, setAddCardForm] = useState({
    clientId: '',
    cardNumber: '',
    expirationDate: '',
    cardCode: '',
    firstName: '',
    lastName: '',
    companyName: '',
  });

  // 5) Clients list (for dropdowns)
  const [clients, setClients] = useState([]);

  // ======================= On Mount =======================
  useEffect(() => {
    fetchTherapistColors();
    fetchClientsList();
    handleFetchData();
  }, []);

  // ============== 1) Build therapist color map ==============
  const fetchTherapistColors = async () => {
    try {
      const res = await API.get('/admin/all-therapists');
      const all = res.data || [];
      let colorMap = {};

      for (let t of all) {
        const nameKey = `${t.first_name} ${t.last_name}`.trim();
        let cVal = '#FFFFFF';
        try {
          const gRes = await API.get(`/admin/therapist-goals/${t.id}`);
          cVal = gRes.data?.color_code || '#FFFFFF';
        } catch (err) {
          // fallback
        }
        colorMap[nameKey] = { colorCode: cVal };
      }
      setTherapistColors(colorMap);
    } catch (err) {
      console.error('Error fetching therapist colors:', err);
    }
  };

  // ============== 2) Fetch clients list ==============
  const fetchClientsList = async () => {
    try {
      const res = await API.get('/admin/all-clients-all');
      setClients(res.data || []);
    } catch (err) {
      console.error('Error fetching clients list:', err);
      alert('Error fetching clients list');
    }
  };

  // ============== 3) Load Unpaid & Debt sessions + Payment Profiles ==============
  const handleFetchData = async () => {
    try {
      // A) Unpaid
      const unpaidRes = await API.get('/admin/daily-report/unpaid');
      const uSessions = unpaidRes.data || [];
      setUnpaidSessions(uSessions);

      // B) Debt with payment_type=card
      const debtRes = await API.get('/admin/daily-report/history?status=DEBT&limit=9999');
      const allDebt = debtRes.data || [];
      const dCard = allDebt.filter((s) => (s.payment_type || '').toLowerCase() === 'card');
      setDebtSessions(dCard);

      // C) gather unique clientIds
      const uniqueClientIds = new Set([
        ...uSessions.map((s) => s.client_id).filter(Boolean),
        ...dCard.map((s) => s.client_id).filter(Boolean),
      ]);

      // D) fetch profiles
      let profileDict = {};
      for (let cId of uniqueClientIds) {
        try {
          const prRes = await API.get(`/admin/clients/${cId}/payment-profiles`);
          if (prRes.data && prRes.data.success) {
            profileDict[cId] = prRes.data.profiles;
          } else {
            profileDict[cId] = [];
          }
        } catch (error) {
          console.error(`Error fetching profiles for clientId=${cId}`, error);
          profileDict[cId] = [];
        }
      }
      setClientProfiles(profileDict);

      alert('Unpaid & Debt sessions loaded!');
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('Error fetching data');
    }
  };

  // ============== Mark as DEBT ==============
  const handleMarkAsDebt = async (session) => {
    if (!session.id) return;
    if (!window.confirm(`Mark session #${session.id} as DEBT?`)) return;
    try {
      await API.put('/admin/daily-report', {
        updates: [{ sessionId: session.id, status: 'DEBT' }],
      });
      alert('Session marked as DEBT');
      handleFetchData();
    } catch (err) {
      console.error('Error marking as DEBT:', err);
      alert('Error marking as DEBT');
    }
  };

  // ============== Charge Card => sets status=SUBMITTED ==============
  const handleChargeCard = async (session, paymentProfileId) => {
    if (!session.client_id) {
      alert('No client_id found for this session.');
      return;
    }
    const amount = session.billed_amount || 0;
    if (amount <= 0) {
      alert('Billed amount is 0, skipping charge.');
      return;
    }
    if (!window.confirm(`Charge $${amount} to this card?`)) return;

    try {
      const res = await API.post(`/admin/clients/${session.client_id}/charge`, {
        amount,
        paymentProfileId,
        sessionId: session.id, // so we can store transaction ID if needed
      });
      if (res.data && res.data.success) {
        alert(`Payment accepted! Transaction ID: ${res.data.transactionId || ''}`);
        // Mark session as SUBMITTED
        await API.put('/admin/daily-report', {
          updates: [{ sessionId: session.id, status: 'SUBMITTED' }],
        });
        handleFetchData();
      } else {
        alert('Charge declined or error occurred.');
      }
    } catch (err) {
      console.error('Error charging card:', err);
      alert('Error charging card');
    }
  };

  // ============== Editing session (optional) ==============
  const startEditing = (row) => {
    setEditingRows((prev) => ({
      ...prev,
      [row.id]: {
        ...row,
        session_date: new Date(row.session_date).toISOString().split('T')[0],
      },
    }));
  };
  const cancelEditing = (id) => {
    setEditingRows((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };
  const handleEditChange = (id, field, value) => {
    setEditingRows((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };
  const saveEdits = async (id) => {
    const rowToSave = editingRows[id];
    if (!rowToSave) return;
    try {
      await API.put('/admin/daily-report/update', {
        id: rowToSave.id,
        status: rowToSave.status,
        billed_amount: rowToSave.billed_amount,
        collected_amount: rowToSave.collected_amount,
        notes: rowToSave.notes,
        service_type: rowToSave.service_type,
        session_date: rowToSave.session_date,
      });
      alert('Session updated!');
      handleFetchData();
      cancelEditing(id);
    } catch (err) {
      console.error('Error saving session edits:', err);
      alert('Error saving session edits');
    }
  };
  const deleteSession = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await API.delete(`/admin/daily-report/${id}`);
      alert('Session deleted');
      handleFetchData();
      cancelEditing(id);
    } catch (err) {
      console.error('Error deleting session:', err);
      alert('Error deleting session');
    }
  };

  // ============== Manage Payment Info ==============
  const handleCreatePaymentProfile = async () => {
    const f = newProfileForm;
    if (!f.clientId || !f.cardNumber || !f.expirationDate || !f.cardCode) {
      return alert('Please fill in required fields (client, card #, exp, CVV).');
    }
    try {
      const res = await API.post(`/admin/clients/${f.clientId}/payment-profile`, {
        cardNumber: f.cardNumber,
        expirationDate: f.expirationDate,
        cardCode: f.cardCode,
        firstName: f.firstName,
        lastName: f.lastName,
        companyName: f.companyName,
      });
      if (res.data && res.data.success) {
        alert(`Profile created! ID: ${res.data.profileId}`);
      } else {
        alert('Failed to create profile');
      }
    } catch (err) {
      console.error('Error creating new profile:', err);
      alert('Error creating profile');
    }
  };

  const handleLinkExistingProfile = async () => {
    if (!linkProfileClientId || !existingProfileId) {
      return alert('Select a client and enter the existing profile ID.');
    }
    try {
      const res = await API.put(`/admin/clients/${linkProfileClientId}/payment-profile`, {
        existingProfileId,
      });
      if (res.data && res.data.success) {
        alert('Existing profile linked successfully!');
      } else {
        alert('Failed to link existing profile');
      }
    } catch (err) {
      console.error('Error linking existing profile:', err);
      alert('Error linking profile');
    }
  };

  const handleAddCardToExistingProfile = async () => {
    const f = addCardForm;
    if (!f.clientId || !f.cardNumber || !f.expirationDate || !f.cardCode) {
      return alert('Please fill in required fields (client, card #, exp, CVV).');
    }
    try {
      const res = await API.post(
        `/admin/clients/${f.clientId}/payment-profile/add-card`,
        {
          cardNumber: f.cardNumber,
          expirationDate: f.expirationDate,
          cardCode: f.cardCode,
          firstName: f.firstName,
          lastName: f.lastName,
          companyName: f.companyName,
        }
      );
      if (res.data && res.data.success) {
        alert(`New card added! PaymentProfileId: ${res.data.newPaymentProfileId}`);
      } else {
        alert(`Failed to add card: ${res.data?.message || ''}`);
      }
    } catch (err) {
      console.error('Error adding new card:', err);
      alert('Error adding card');
    }
  };

  // ------------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------------
  return (
    <div className="admin-payment-page-container">
      <h2>Admin Payment Page</h2>

      <button onClick={handleFetchData} style={{ marginBottom: '1rem' }}>
        Refresh Unpaid & Debt Sessions
      </button>

      {/* ==================== UNPAID SESSIONS ==================== */}
      <section>
        <h3>Unpaid Sessions</h3>
        {unpaidSessions.length === 0 ? (
          <p>No unpaid sessions found.</p>
        ) : (
          <table className="daily-report-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Therapist</th>
                <th>Client</th>
                <th>Billed</th>
                <th>Collected</th>
                <th>Payment</th>
                <th>Notes</th>
                <th>Payment Profiles</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {unpaidSessions.map((s) => {
                const isEditing = !!editingRows[s.id];
                const editRow = editingRows[s.id] || {};
                const therapistName = `${s.therapist_first_name} ${s.therapist_last_name}`.trim();
                const profiles = clientProfiles[s.client_id] || [];

                return (
                  <tr
                    key={s.id}
                    style={{
                      ...getTherapistRowStyle(therapistName, therapistColors),
                      borderBottom: '1px solid #ccc',
                    }}
                  >
                    <td>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editRow.session_date}
                          onChange={(e) => handleEditChange(s.id, 'session_date', e.target.value)}
                        />
                      ) : (
                        formatSessionDate(s.session_date)
                      )}
                    </td>
                    <td>{therapistName}</td>
                    <td>{s.client_name}</td>
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          style={{ width: '80px' }}
                          value={editRow.billed_amount}
                          onChange={(e) => handleEditChange(s.id, 'billed_amount', e.target.value)}
                        />
                      ) : (
                        `$${s.billed_amount}`
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          style={{ width: '80px' }}
                          value={editRow.collected_amount || 0}
                          onChange={(e) =>
                            handleEditChange(s.id, 'collected_amount', e.target.value)
                          }
                        />
                      ) : (
                        `$${s.collected_amount || 0}`
                      )}
                    </td>
                    <td>
                      <span style={getPaymentStyle(s.payment_type)}>{s.payment_type || ''}</span>
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          style={{ width: '100px' }}
                          value={editRow.notes || ''}
                          onChange={(e) => handleEditChange(s.id, 'notes', e.target.value)}
                        />
                      ) : (
                        s.notes || ''
                      )}
                    </td>
                    <td>
                      {profiles.length === 0 ? (
                        <div>No payment profiles</div>
                      ) : (
                        profiles.map((p) => (
                          <div key={p.paymentProfileId} style={{ marginBottom: '0.5rem' }}>
                            <strong>{p.firstName} {p.lastName}</strong>
                            {p.companyName && <> ({p.companyName})</>}
                            <br />
                            Card: {p.maskedCard} (Exp: {p.expiration})
                            <br />
                            <button
                              onClick={() => handleChargeCard(s, p.paymentProfileId)}
                              style={{ marginTop: '0.3rem' }}
                            >
                              Charge This Card
                            </button>
                          </div>
                        ))
                      )}
                    </td>
                    <td style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                      {isEditing ? (
                        <>
                          <button onClick={() => saveEdits(s.id)}>Save</button>
                          <button onClick={() => deleteSession(s.id)}>Delete</button>
                          <button onClick={() => cancelEditing(s.id)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEditing(s)}>Edit</button>
                          <button onClick={() => handleMarkAsDebt(s)}>Mark as Debt</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <hr style={{ margin: '2rem 0' }} />

      {/* ==================== DEBT SESSIONS (Card) ==================== */}
      <section>
        <h3>Debt Sessions (Card)</h3>
        {debtSessions.length === 0 ? (
          <p>No debt sessions with card found.</p>
        ) : (
          <table className="daily-report-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Therapist</th>
                <th>Client</th>
                <th>Billed</th>
                <th>Collected</th>
                <th>Payment</th>
                <th>Notes</th>
                <th>Payment Profiles</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {debtSessions.map((s) => {
                const therapistName = `${s.therapist_first_name} ${s.therapist_last_name}`.trim();
                const profiles = clientProfiles[s.client_id] || [];

                return (
                  <tr
                    key={s.id}
                    style={{
                      ...getTherapistRowStyle(therapistName, therapistColors),
                      borderBottom: '1px solid #ccc',
                    }}
                  >
                    <td>{formatSessionDate(s.session_date)}</td>
                    <td>{therapistName}</td>
                    <td>{s.client_name}</td>
                    <td>${s.billed_amount}</td>
                    <td>${s.collected_amount || 0}</td>
                    <td>
                      <span style={getPaymentStyle(s.payment_type)}>{s.payment_type || ''}</span>
                    </td>
                    <td>{s.notes || ''}</td>
                    <td>
                      {profiles.length === 0 ? (
                        <div>No payment profiles</div>
                      ) : (
                        profiles.map((p) => (
                          <div key={p.paymentProfileId} style={{ marginBottom: '0.5rem' }}>
                            <strong>{p.firstName} {p.lastName}</strong>
                            {p.companyName && <> ({p.companyName})</>}
                            <br />
                            Card: {p.maskedCard} (Exp: {p.expiration})
                            <br />
                            <button
                              onClick={() => handleChargeCard(s, p.paymentProfileId)}
                              style={{ marginTop: '0.3rem' }}
                            >
                              Charge This Card
                            </button>
                          </div>
                        ))
                      )}
                    </td>
                    <td>
                      {/* If you want to remove debt or set status=PAID, do it here */}
                      <em>(No additional actions here yet)</em>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <hr style={{ margin: '2rem 0' }} />

      {/* =========== MANAGE PAYMENT INFO =========== */}
      <section>
        <h3>Manage Payment Info</h3>

        {/* (A) Create new profile */}
        <div style={{ border: '1px solid #ccc', padding: '1rem', marginBottom: '1rem' }}>
          <h4>Create New Authorize.net Profile</h4>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>Client: </label>
            <select
              value={newProfileForm.clientId}
              onChange={(e) =>
                setNewProfileForm((prev) => ({ ...prev, clientId: e.target.value }))
              }
              style={{ marginRight: '1rem' }}
            >
              <option value="">-- Select Client --</option>
              {clients.map((cl) => (
                <option key={cl.id} value={cl.id}>
                  {cl.client_name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>Card Number</label>
            <input
              type="text"
              value={newProfileForm.cardNumber}
              onChange={(e) =>
                setNewProfileForm((prev) => ({ ...prev, cardNumber: e.target.value }))
              }
              style={{ marginLeft: '0.5rem' }}
            />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>Exp Date (YYYY-MM)</label>
            <input
              type="text"
              value={newProfileForm.expirationDate}
              onChange={(e) =>
                setNewProfileForm((prev) => ({ ...prev, expirationDate: e.target.value }))
              }
              style={{ marginLeft: '0.5rem', width: '7rem' }}
            />
            <label style={{ marginLeft: '1rem' }}>CVV</label>
            <input
              type="text"
              value={newProfileForm.cardCode}
              onChange={(e) =>
                setNewProfileForm((prev) => ({ ...prev, cardCode: e.target.value }))
              }
              style={{ marginLeft: '0.5rem', width: '4rem' }}
            />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>First Name</label>
            <input
              type="text"
              value={newProfileForm.firstName}
              onChange={(e) =>
                setNewProfileForm((prev) => ({ ...prev, firstName: e.target.value }))
              }
              style={{ marginLeft: '0.5rem' }}
            />
            <label style={{ marginLeft: '1rem' }}>Last Name</label>
            <input
              type="text"
              value={newProfileForm.lastName}
              onChange={(e) =>
                setNewProfileForm((prev) => ({ ...prev, lastName: e.target.value }))
              }
              style={{ marginLeft: '0.5rem' }}
            />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>Company</label>
            <input
              type="text"
              value={newProfileForm.companyName}
              onChange={(e) =>
                setNewProfileForm((prev) => ({ ...prev, companyName: e.target.value }))
              }
              style={{ marginLeft: '0.5rem' }}
            />
          </div>
          <button onClick={handleCreatePaymentProfile}>Create Profile</button>
        </div>

        {/* (B) Link existing */}
        <div style={{ border: '1px solid #ccc', padding: '1rem', marginBottom: '1rem' }}>
          <h4>Link Existing Authorize.net Profile</h4>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>Client: </label>
            <select
              value={linkProfileClientId}
              onChange={(e) => setLinkProfileClientId(e.target.value)}
              style={{ marginRight: '1rem' }}
            >
              <option value="">-- Select Client --</option>
              {clients.map((cl) => (
                <option key={cl.id} value={cl.id}>
                  {cl.client_name}
                </option>
              ))}
            </select>
            <label>Profile ID</label>
            <input
              type="text"
              value={existingProfileId}
              onChange={(e) => setExistingProfileId(e.target.value)}
              style={{ marginLeft: '0.5rem' }}
            />
          </div>
          <button onClick={handleLinkExistingProfile}>Link Profile</button>
        </div>

        {/* (C) Add card to existing */}
        <div style={{ border: '1px solid #ccc', padding: '1rem' }}>
          <h4>Add Card to Existing Profile</h4>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>Client: </label>
            <select
              value={addCardForm.clientId}
              onChange={(e) =>
                setAddCardForm((prev) => ({ ...prev, clientId: e.target.value }))
              }
              style={{ marginRight: '1rem' }}
            >
              <option value="">-- Select Client --</option>
              {clients.map((cl) => (
                <option key={cl.id} value={cl.id}>
                  {cl.client_name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>Card Number</label>
            <input
              type="text"
              value={addCardForm.cardNumber}
              onChange={(e) => setAddCardForm((prev) => ({ ...prev, cardNumber: e.target.value }))}
              style={{ marginLeft: '0.5rem' }}
            />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>Exp Date (YYYY-MM)</label>
            <input
              type="text"
              value={addCardForm.expirationDate}
              onChange={(e) =>
                setAddCardForm((prev) => ({ ...prev, expirationDate: e.target.value }))
              }
              style={{ marginLeft: '0.5rem', width: '7rem' }}
            />
            <label style={{ marginLeft: '1rem' }}>CVV</label>
            <input
              type="text"
              value={addCardForm.cardCode}
              onChange={(e) =>
                setAddCardForm((prev) => ({ ...prev, cardCode: e.target.value }))
              }
              style={{ marginLeft: '0.5rem', width: '4rem' }}
            />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>First Name</label>
            <input
              type="text"
              value={addCardForm.firstName}
              onChange={(e) =>
                setAddCardForm((prev) => ({ ...prev, firstName: e.target.value }))
              }
              style={{ marginLeft: '0.5rem' }}
            />
            <label style={{ marginLeft: '1rem' }}>Last Name</label>
            <input
              type="text"
              value={addCardForm.lastName}
              onChange={(e) =>
                setAddCardForm((prev) => ({ ...prev, lastName: e.target.value }))
              }
              style={{ marginLeft: '0.5rem' }}
            />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label>Company</label>
            <input
              type="text"
              value={addCardForm.companyName}
              onChange={(e) =>
                setAddCardForm((prev) => ({ ...prev, companyName: e.target.value }))
              }
              style={{ marginLeft: '0.5rem' }}
            />
          </div>
          <button onClick={handleAddCardToExistingProfile}>Add Card</button>
        </div>
      </section>
    </div>
  );
}

export default AdminPaymentPage;
